import { createSlice } from '@reduxjs/toolkit';

import {
  ErrorResponse,
  ProductsRateResponse
} from '../_declarations/financialCalculator';
import { AsyncStatus } from '../_declarations/globals';
import {
  postDefaultCustomRate,
  postProductsRate
} from '../api/financialCalculator';

interface FinancialCalculatorState {
  error?: ErrorResponse | null;
  invalidDiscountCode?: boolean;
  invalidManufacturerCode?: boolean | null;
  productsRate?: ProductsRateResponse;
  status: {
    postDefaultCustomRate?: AsyncStatus;
    postProductsRate?: AsyncStatus;
  };
}

const initialState = {
  error: null,
  invalidManufacturerCode: false,
  productsRate: undefined,
  status: {
    postDefaultCustomRate: undefined,
    postProductsRate: undefined
  }
} as FinancialCalculatorState;

const financialCalculatorSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(postDefaultCustomRate.fulfilled, (state, action) => {
        if (state?.productsRate?.packageSelection && action.payload) {
          const newList: ProductsRateResponse.PackageSelection[] = [
            ...state.productsRate.packageSelection
          ];
          const index = newList.findIndex(
            (i: ProductsRateResponse.PackageSelection) =>
              i.uuid === action.payload.uuid
          );
          if (index > -1) {
            const newItem: ProductsRateResponse.PackageSelection = {
              ...action.payload,
              criteriaAdjustment: newList[index].criteriaAdjustment
            };
            newList.splice(index, 1, newItem);
          }
          const newProductRate: ProductsRateResponse = {
            ...state.productsRate,
            packageSelection: newList
          };
          state.productsRate = newProductRate;
          state.status.postDefaultCustomRate = 'success';
        }
      })
      .addCase(postDefaultCustomRate.pending, (state) => {
        state.status.postDefaultCustomRate = 'loading';
        state.invalidDiscountCode = false;
      })
      .addCase(postDefaultCustomRate.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.status.postDefaultCustomRate = 'failed';
        }
        const errorObject = action.payload as ErrorResponse;
        const errorMessage = errorObject?.errors?.[0]?.desc;
        const regex = /E003/i;
        if (regex.test(errorMessage)) {
          state.invalidDiscountCode = true;
          const index = state.productsRate?.packageSelection?.findIndex(
            (p) => p.uuid === action.meta.arg.uuid
          );
          if (index !== undefined && index > -1 && state.productsRate) {
            state.productsRate.packageSelection[
              index
            ].result.teaser.regularPayment.discountCode = '';
          }
        }
      })
      .addCase(postProductsRate.fulfilled, (state, action) => {
        if (action.payload) {
          state.productsRate = action.payload;
          state.status.postProductsRate = 'success';
        }
      })
      .addCase(postProductsRate.pending, (state) => {
        state.status.postProductsRate = 'loading';
        state.invalidManufacturerCode = false;
      })
      .addCase(postProductsRate.rejected, (state, action) => {
        state.status.postProductsRate = 'failed';
        const errorObject = action.payload as ErrorResponse;
        const errorMessage = errorObject?.errors?.[0]?.desc;
        const regexSAP = /ERR002/i;
        const regexMiles = /RateBook Service Error Code/i;
        if (regexSAP.test(errorMessage) || regexMiles.test(errorMessage)) {
          state.invalidManufacturerCode = true;
        }
      });
  },
  initialState,
  name: 'financialCalculator',
  reducers: {
    postDefaultCustomRateError: (state, action) => {
      state.status.postDefaultCustomRate = 'failed';
      const errorObject = action.payload as ErrorResponse;
      const errorMessage = errorObject?.errors?.[0]?.desc;
      const regex = /E003/i;
      if (regex.test(errorMessage)) {
        state.invalidDiscountCode = true;
      }
    },
    postProductsRateError: (state, action) => {
      state.status.postProductsRate = 'failed';
      const errorObject = action.payload as ErrorResponse;
      const errorMessage = errorObject?.errors?.[0]?.desc;
      const regex = /ERR002/i;
      if (regex.test(errorMessage)) {
        state.invalidManufacturerCode = true;
      }
    },
    resetFCState: () => initialState,
    resetStatusesAPIs: (state) => {
      state.status = {
        postDefaultCustomRate: 'success',
        postProductsRate: 'success'
      };
    },

    updateDefaultCustomRate: (state, action) => {
      if (state?.productsRate?.packageSelection) {
        const newList: ProductsRateResponse.PackageSelection[] = [
          ...(state.productsRate.packageSelection || [])
        ];
        const index = newList.findIndex(
          (i: ProductsRateResponse.PackageSelection) =>
            i.uuid === action.payload.uuid
        );
        if (index > -1) {
          const newItem: ProductsRateResponse.PackageSelection = {
            ...action.payload,
            criteriaAdjustment: newList[index].criteriaAdjustment
          };
          newList.splice(index, 1, newItem);
        }
        const newProductRate: ProductsRateResponse = {
          ...state.productsRate,
          packageSelection: newList
        };
        state.productsRate = newProductRate;
      }

      state.status.postDefaultCustomRate = 'success';
    },
    updateProductsList: (state, action) => {
      state.productsRate = action.payload;
      state.status.postProductsRate = 'success';
    }
  }
});

export const {
  postDefaultCustomRateError,
  postProductsRateError,
  resetFCState,
  resetStatusesAPIs,
  updateDefaultCustomRate,
  updateProductsList
} = financialCalculatorSlice.actions;
export default financialCalculatorSlice.reducer;
